/**
 * First-login specialty picker.
 *
 * Rendered at most once per user (tracked by
 * `preferences.specialtyPromptDismissedAt` on success or skip). Mirrors the
 * voiceprint onboarding modal — same Dialog primitive, same minimal copy,
 * same skip path.
 */

import React, { useState } from 'react';
import { Stethoscope } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from '@/hooks/useTranslation';
import {
  useUpdateMyPreferences,
  useUpdateMyProfile,
} from '@/hooks/useMyProfile';
import { SpecialtyPicker } from '@/components/SpecialtyPicker';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const SpecialtyOnboardingModal: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<string | null>(null);
  const updateProfile = useUpdateMyProfile();
  const updatePrefs = useUpdateMyPreferences();

  const handleSkip = async () => {
    try {
      await updatePrefs.mutateAsync({
        specialtyPromptDismissedAt: new Date().toISOString(),
      });
    } finally {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      // Stamp dismissedAt alongside the specialty. The hook's
      // "alreadyPicked" check now ignores the system default `'general'`,
      // so a doctor who confirms `general` would otherwise see this
      // modal again on the next login. The dismissedAt flag is the
      // single "stop asking" signal across both Confirm and Skip.
      await Promise.all([
        updateProfile.mutateAsync({ specialty: pending }),
        updatePrefs.mutateAsync({
          specialtyPromptDismissedAt: new Date().toISOString(),
        }),
      ]);
    } finally {
      onClose();
    }
  };

  const busy = updateProfile.isPending || updatePrefs.isPending;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,520px)] max-h-[85vh] overflow-auto rounded-2xl p-5 sm:p-6 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ backgroundColor: 'var(--color-bg-primary)' }}
        >
          <Dialog.Title asChild>
            <h2
              className="text-lg sm:text-xl font-semibold flex items-center gap-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <Stethoscope className="w-5 h-5" />
              {t('specialty.onboarding.title')}
            </h2>
          </Dialog.Title>
          <Dialog.Description asChild>
            <p
              className="mt-2 text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t('specialty.onboarding.description')}
            </p>
          </Dialog.Description>

          <div className="mt-5">
            <SpecialtyPicker
              value={pending}
              onChange={setPending}
              disabled={busy}
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="secondary"
              type="button"
              onClick={handleSkip}
              disabled={busy}
            >
              {t('specialty.onboarding.skip')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!pending || busy}
            >
              {t('specialty.onboarding.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
